import { Head, router, usePage } from '@inertiajs/react';
import { useState } from 'react';

import { PhaseTracker } from '@/components/admin/detail/PhaseTracker';
import {
  type LedgerItem,
  MetaDot,
  RecordActions,
  RecordBackLink,
  RecordBody,
  RecordFiles,
  RecordLedger,
  RecordMasthead,
  RecordSection,
  RecordSheet,
  RecordText,
} from '@/components/admin/detail/RecordSheet';
import {
  INFRACTION_LABELS,
  formatReportDate,
  type ReportStatus,
  type WhistleblowReport,
} from '@/components/admin/whistleblow/report';
import { Button } from '@/components/ui/Button';
import { trackUmamiEvent } from '@/lib/analytics';

interface PageProps {
  report: WhistleblowReport;
  [key: string]: unknown;
}

export default function WhistleblowReportDetail() {
  const { report } = usePage<PageProps>().props;
  const [loading, setLoading] = useState(false);

  function changeStatus(next: ReportStatus) {
    setLoading(true);
    trackUmamiEvent('admin_status_change', {
      record_type: 'whistleblow_reports',
      from_status: report.status,
      to_status: next,
    });
    router.patch(
      `/whistleblow-reports/${report.id}/status`,
      { status: next },
      { preserveScroll: true, onFinish: () => setLoading(false) },
    );
  }

  const ledger: LedgerItem[] = [
    { label: 'Email do denunciante', value: report.email ?? 'Anónimo' },
    { label: 'Conservar até', value: formatReportDate(report.purgeAfter) },
  ];
  if (report.acknowledgedAt) {
    ledger.push({
      label: 'Receção acusada por',
      value: `${report.acknowledgedBy} · ${formatReportDate(report.acknowledgedAt)}`,
    });
  }
  if (report.closedAt) {
    ledger.push({
      label: 'Concluída por',
      value: `${report.closedBy} · ${formatReportDate(report.closedAt)}`,
    });
  }

  return (
    <>
      <Head title={`Denúncia ${report.referenceCode} — Administração`} />
      <RecordBackLink href="/whistleblow-reports" label="Voltar a Denúncias" />

      <RecordSheet>
        <RecordMasthead
          typeTag="Denúncia"
          title={INFRACTION_LABELS[report.infractionType] ?? report.infractionType}
          topRight={
            <span className="text-on-surface-light/70 text-[10px] font-semibold tracking-wide tabular-nums">
              {report.referenceCode}
            </span>
          }
          meta={
            <>
              <span>{report.email ? report.email : 'Anónima'}</span>
              <MetaDot />
              <span>{formatReportDate(report.submittedAt)}</span>
            </>
          }
          tracker={<PhaseTracker status={report.status} />}
        />

        <RecordBody>
          <RecordSection label="Descrição">
            <RecordText>{report.summary}</RecordText>
          </RecordSection>
          {report.factsWhen && (
            <RecordSection label="Quando ocorreu">
              <RecordText>{report.factsWhen}</RecordText>
            </RecordSection>
          )}
          {report.factsWho && (
            <RecordSection label="Pessoas envolvidas">
              <RecordText>{report.factsWho}</RecordText>
            </RecordSection>
          )}
          {report.factsEvidence && (
            <RecordSection label="Elementos de prova">
              <RecordText>{report.factsEvidence}</RecordText>
            </RecordSection>
          )}
          {report.files.length > 0 && (
            <RecordSection label="Anexos">
              <RecordFiles files={report.files} />
            </RecordSection>
          )}
        </RecordBody>

        <RecordLedger items={ledger} />

        <RecordActions>
          {report.status === 'pending' && (
            <Button
              variant="primary"
              size="sm"
              disabled={loading}
              onClick={() => changeStatus('acknowledged')}
            >
              <span className="material-symbols-outlined text-[18px]">verified</span>
              Acusar receção
            </Button>
          )}
          {(report.status === 'pending' || report.status === 'acknowledged') && (
            <Button
              variant="white"
              size="sm"
              disabled={loading}
              onClick={() => changeStatus('in_progress')}
            >
              <span className="material-symbols-outlined text-[18px]">schedule</span>
              Marcar como em análise
            </Button>
          )}
          {report.status !== 'closed' && (
            <Button
              variant="green"
              size="sm"
              disabled={loading}
              onClick={() => changeStatus('closed')}
            >
              <span className="material-symbols-outlined text-[18px]">check_circle</span>
              Concluir
            </Button>
          )}
        </RecordActions>
      </RecordSheet>
    </>
  );
}
